import React from 'react';
import {
    FileText, Workflow, BarChart3, MessageSquare, Brain, Shield,
    ChevronRight, ArrowUpRight
} from 'lucide-react';

const ValuePropositionSection = ({ onNavigate = () => { } }) => {
    const benefits = [
        {
            title: "Custom Business Solutions",
            description: "Tailored digital solutions designed specifically for your business needs, integrating seamlessly with your existing systems and workflows.",
            icon: <FileText className="w-10 h-10" />,
            color: "blue",
            stats: "100% customizable",
            path: "/business-solutions",
            inputLabel: "Requirements",
            outputLabel: "Custom Solution"
        },
        {
            title: "Automation",
            description: "Streamline repetitive tasks and workflows with intelligent automation that saves time, reduces errors, and allows your team to focus on high-value activities.",
            icon: <Workflow className="w-10 h-10" />,
            color: "indigo",
            stats: "85% efficiency increase",
            path: "/automation",
            inputLabel: "Manual Tasks",
            outputLabel: "Automated Workflow"
        },
        {
            title: "Dashboards",
            description: "Transform complex data into intuitive visual dashboards that provide real-time insights and help drive informed business decisions at every level.",
            icon: <BarChart3 className="w-10 h-10" />,
            color: "purple",
            stats: "360� business visibility",
            path: "/dashboards",
            inputLabel: "Raw Data",
            outputLabel: "Actionable Insights"
        },
        {
            title: "Gen AI Chatbots",
            description: "Deploy intelligent conversational interfaces that understand your customers' needs, provide immediate assistance, and enhance engagement across all channels.",
            icon: <MessageSquare className="w-10 h-10" />,
            color: "pink",
            stats: "24/7 customer support",
            path: "/ai-chatbots",
            inputLabel: "User Queries",
            outputLabel: "Instant Support"
        },
        {
            title: "AI Solutions",
            description: "AI implementation that revolutionizes your entire business - from operations and insights to customer experiences and resource optimization.",
            icon: <Brain className="w-10 h-10" />,
            color: "amber",
            stats: "Complete transformation",
            path: "/ai-solutions",
            inputLabel: "Business Challenge",
            outputLabel: "AI Transformation"
        },
        {
            title: "Security",
            description: "Protect your valuable data and systems with enterprise-grade security solutions that ensure compliance, prevent breaches, and maintain customer trust.",
            icon: <Shield className="w-10 h-10" />,
            color: "emerald",
            stats: "99.9% security rating",
            path: "/security",
            inputLabel: "Threat Vectors",
            outputLabel: "Protected Assets"
        }
    ];

    // Get color classes based on benefit color
    const getColorClasses = (color, element) => {
        const colorMap = {
            blue: {
                text: "text-blue-400",
                bgLight: "bg-blue-400/10",
                bgMedium: "bg-blue-500/20",
                bgGradient: "from-blue-600 to-blue-400",
                border: "border-blue-500/30",
                shadow: "" // Removed shadow class
            },
            indigo: {
                text: "text-indigo-400",
                bgLight: "bg-indigo-400/10",
                bgMedium: "bg-indigo-500/20",
                bgGradient: "from-indigo-600 to-indigo-400",
                border: "border-indigo-500/30",
                shadow: "" // Removed shadow class
            },
            purple: {
                text: "text-purple-400",
                bgLight: "bg-purple-400/10",
                bgMedium: "bg-purple-500/20",
                bgGradient: "from-purple-600 to-purple-400",
                border: "border-purple-500/30",
                shadow: "" // Removed shadow class
            },
            pink: {
                text: "text-pink-400",
                bgLight: "bg-pink-400/10",
                bgMedium: "bg-pink-500/20",
                bgGradient: "from-pink-600 to-pink-400",
                border: "border-pink-500/30",
                shadow: "" // Removed shadow class
            },
            amber: {
                text: "text-amber-400",
                bgLight: "bg-amber-400/10",
                bgMedium: "bg-amber-500/20",
                bgGradient: "from-amber-600 to-amber-400",
                border: "border-amber-500/30",
                shadow: "" // Removed shadow class
            },
            emerald: {
                text: "text-emerald-400",
                bgLight: "bg-emerald-400/10",
                bgMedium: "bg-emerald-500/20",
                bgGradient: "from-emerald-600 to-emerald-400",
                border: "border-emerald-500/30",
                shadow: "" // Removed shadow class
            }
        };
        return colorMap[color][element];
    };

    // Handle navigation to other pages
    const handleNavigate = (path) => {
        onNavigate(path);
    };

    // Pipeline-styled Benefit Card
    const BenefitCard = ({ benefit }) => (
        <div
            className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:translate-y-[-8px]"
            onClick={() => handleNavigate(benefit.path)}
        >
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800/90 to-slate-900/90 backdrop-blur-sm shadow-lg" />
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-blue-600/20 transition-opacity duration-500" />
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
            {/* Pipeline connector on left side */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-6 h-20 bg-slate-800 rounded-r-md border border-blue-500/30 overflow-hidden">
                <div className="absolute inset-y-0 right-0 w-1 bg-blue-500/30"></div>
                <div className="absolute top-1/2 left-0 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
            </div>
            <div className="relative p-6 flex flex-col items-center text-center h-full">
                {/* Changed here - removed the shadow-lg class and replaced with predefined shadows */}
                <div className={`bg-gradient-to-tr from-slate-800 to-slate-700 p-3 rounded-lg border border-slate-600/30 mb-4`}>
                    <div className={getColorClasses(benefit.color, "text")}>
                        {benefit.icon}
                    </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center">
                    {benefit.title}
                    <ArrowUpRight className="w-4 h-4 ml-2 opacity-60" />
                </h3>
                <p className="text-slate-300 leading-relaxed">{benefit.description}</p>
                {/* Data flow indicators at bottom */}
                <div className="mt-auto pt-4 w-full">
                    <div className="w-full h-1 bg-slate-700/50 rounded-full overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${getColorClasses(benefit.color, "bgGradient")} w-0 group-hover:w-full transition-all duration-1000 rounded-full`}></div>
                    </div>
                    <div className="mt-3 flex justify-between items-center">
                        <div className="flex items-center">
                            <div className={`w-2 h-2 ${getColorClasses(benefit.color, "text")} rounded-full mr-1 animate-pulse`}></div>
                            <span className={`text-xs ${getColorClasses(benefit.color, "text")}`}>{benefit.inputLabel}</span>
                        </div>
                        <div className="flex items-center">
                            <span className={`text-xs ${getColorClasses(benefit.color, "text")}`}>{benefit.outputLabel}</span>
                            <div className={`w-2 h-2 ${getColorClasses(benefit.color, "text")} rounded-full ml-1 animate-pulse`}></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <section className="relative z-10 py-24 px-4 overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
            </div>
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-16">
                    {/* Title without glow effect */}
                    <div className="relative inline-block">
                        <h2 className="relative text-3xl md:text-5xl font-bold mb-6 text-white">
                            We Got You Covered
                        </h2>
                    </div>
                    {/* Description */}
                    <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                        Transform your business with our suite of innovative digital solutions designed to
                        optimize operations, enhance customer engagement, and drive sustainable growth
                    </p>
                </div>
                {/* Benefit cards with pipeline styling */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
                    {benefits.map((benefit, index) => (
                        <BenefitCard key={index} benefit={benefit} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ValuePropositionSection;